import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getShiftStatus } from './shift_status';
import { formatDateTimeOrdinal } from './date_util';
import { ExcelButton } from './excel_button';
import { fetchAllShifts } from '../redux/reducers/shifts_reducer';
//#B7966A

const SHIFTS_PER_PAGE = 50;

const getPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  if (current > 3) pages.push('...');
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
};

const formatDateTime = formatDateTimeOrdinal;

const toIsoDate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const Shifts = () => {
  const navigate = useNavigate();
  const clientData = useSelector(state => state.user.clientData);
  const rawColor = clientData && clientData.color ? clientData.color.trim() : '';
  const defaultPinColor = rawColor ? (rawColor.startsWith('#') ? rawColor : '#' + rawColor) : '#B7966A';

  const today = new Date();
  const yearStartDefault = toIsoDate(new Date(today.getFullYear(), 0, 1));
  const yearEndDefault = toIsoDate(new Date(today.getFullYear(), 11, 31));

  const dispatch = useDispatch();
  const shifts = useSelector(state => state.shifts.list);
  const shiftsLoaded = useSelector(state => state.shifts.loaded);
  const shiftsLoading = useSelector(state => state.shifts.loading);
  const isLoading = !shiftsLoaded;

  useEffect(() => {
    if (!shiftsLoaded && !shiftsLoading) {
      fetchAllShifts(dispatch);
    }
  }, [shiftsLoaded, shiftsLoading, dispatch]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);
  const [startDate, setStartDate] = useState(yearStartDefault);
  const [endDate, setEndDate] = useState(yearEndDefault);
  const filterRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFilterOpen(false);
      }
    };
    if (filterOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [filterOpen]);

  const filteredShifts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const fromTime = startDate ? new Date(`${startDate}T00:00:00`).getTime() : null;
    const toTime = endDate ? new Date(`${endDate}T23:59:59.999`).getTime() : null;
    return shifts.filter(item => {
      if (!item) return false;
      const startVal = item[6] && item[6].value ? new Date(item[6].value).getTime() : null;
      if (fromTime !== null && (startVal === null || startVal < fromTime)) return false;
      if (toTime !== null && (startVal === null || startVal > toTime)) return false;
      if (!term) return true;
      const fields = [
        item[378] ? item[378].value : '',
        item[321] ? item[321].value : '',
        item[898] ? item[898].value : '',
        item[107] ? item[107].value : '',
        item[108] ? item[108].value : '',
        item[543] ? item[543].value : '',
        item[6] ? formatDateTime(item[6].value) : '',
        item[7] ? formatDateTime(item[7].value) : '',
      ];
      return fields.some(val => (val || '').toLowerCase().includes(term));
    });
  }, [shifts, searchTerm, startDate, endDate]);

  const sortedShifts = useMemo(() => {
    return [...filteredShifts].sort((a, b) => {
      const aStart = a && a[6] && a[6].value ? new Date(a[6].value).getTime() : Infinity;
      const bStart = b && b[6] && b[6].value ? new Date(b[6].value).getTime() : Infinity;
      return aStart - bStart;
    });
  }, [filteredShifts]);

  const totalPages = Math.max(1, Math.ceil(sortedShifts.length / SHIFTS_PER_PAGE));
  const startIndex = (page - 1) * SHIFTS_PER_PAGE;
  const paginatedShifts = sortedShifts.slice(startIndex, startIndex + SHIFTS_PER_PAGE);

  useEffect(() => { setPage(1); }, [searchTerm, startDate, endDate]);
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const redirectToShiftPage = (shift) => {
    const recordId = shift && shift[3] ? shift[3].value : null;
    if (recordId) navigate(`/shift/${recordId}`, { state: { shift } });
  };

  const resetDates = () => {
    setStartDate(yearStartDefault);
    setEndDate(yearEndDefault);
  };

  const exportRows = () => sortedShifts.map(shift => {
    const { statusText } = getShiftStatus(shift, defaultPinColor);
    return {
      'Vendor Name': shift && shift[378] ? shift[378].value : '',
      'Location Code': shift && shift[321] ? shift[321].value : '',
      'Status': statusText,
      'Address': shift && shift[898] ? shift[898].value : '',
      'Armed/Unarmed': shift && shift[107] ? shift[107].value : '',
      'Guard Type': shift && shift[108] ? shift[108].value : '',
      'Temporary/Recurring': shift && shift[1295] ? shift[1295].value : '',
      'Shift Start (local time)': shift && shift[6] ? formatDateTimeOrdinal(shift[6].value) : '',
      'Shift End (local time)': shift && shift[7] ? formatDateTimeOrdinal(shift[7].value) : '',
    };
  });

  return (
    <div className="shifts-home">
      <div className="locationsList">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <h2 style={{ margin: 0 }}>Shifts</h2>
          <ExcelButton rows={exportRows} fileName="shifts.xlsx" sheetName="Shifts" />
        </div>
        <div style={{ display: 'flex', alignItems: 'stretch', gap: '12px', marginTop: '16px' }} ref={filterRef}>
          <div className="searchInputWrapper inputWrapper" style={{ flex: 1, marginBottom: 0 }}>
            <input placeholder="Search shifts..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setFilterOpen(o => !o)}
              aria-label="Filter by date range"
              style={{ background: '#B7966A', color: 'white', border: 'none', borderRadius: '5px', padding: '13px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px', fontWeight: 'bold', whiteSpace: 'nowrap' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              Filter by Date
            </button>
            {filterOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: 'white', border: '1px solid #ccc', borderRadius: '8px', padding: '12px', zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: '240px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '13px', color: '#444' }}>
                    Start date
                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={{ display: 'block', width: '100%', marginTop: '4px', padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }} />
                  </label>
                  <label style={{ fontSize: '13px', color: '#444' }}>
                    End date
                    <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={{ display: 'block', width: '100%', marginTop: '4px', padding: '6px', border: '1px solid #ccc', borderRadius: '4px' }} />
                  </label>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                    <button type="button" onClick={resetDates} style={{ background: 'none', border: 'none', color: '#B7966A', cursor: 'pointer', fontSize: '13px' }}>Reset to current year</button>
                    <button type="button" onClick={() => setFilterOpen(false)} style={{ background: '#B7966A', color: 'white', border: 'none', borderRadius: '4px', padding: '6px 12px', cursor: 'pointer', fontSize: '13px' }}>Done</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="skeleton-wrapper" style={{ marginTop: '12px' }}>
            <table className="skeleton-table">
              <tbody>
                <tr>
                  <th>Vendor Name</th>
                  <th>Location Code</th>
                  <th>Status</th>
                  <th>Address</th>
                  <th>Armed/Unarmed</th>
                  <th>Guard Type</th>
                  <th>Temporary/Recurring</th>
                  <th>Shift Start (local time)</th>
                  <th>Shift End (local time)</th>
                </tr>
                {Array.from({ length: 10 }).map((_, idx) => (
                  <tr key={idx}>
                    {Array.from({ length: 9 }).map((_, c) => (
                      <td key={c}><div className={`skeleton-cell ${c % 3 === 0 ? 'skeleton-cell-short' : c % 3 === 1 ? 'skeleton-cell-medium' : 'skeleton-cell-long'}`}></div></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '10px', marginTop: '10px' }}>
              Showing {sortedShifts.length > 0 ? startIndex + 1 : 0}-{Math.min(startIndex + SHIFTS_PER_PAGE, sortedShifts.length)} of {sortedShifts.length} shifts
            </p>
            <div className="tableScroll">
              <table id="tableWrapper">
                <tbody>
                  <tr>
                    <th>Vendor Name</th>
                    <th>Location Code</th>
                    <th>Status</th>
                    <th className="addressCell">Address</th>
                    <th>Armed/Unarmed</th>
                    <th>Guard Type</th>
                    <th>Temporary/Recurring</th>
                    <th>Shift Start (local time)</th>
                    <th>Shift End (local time)</th>
                  </tr>
                  {paginatedShifts.map((shift, idx) => {
                    const { statusText, statusColor } = getShiftStatus(shift, defaultPinColor);
                    return (
                      <tr key={idx} onClick={() => redirectToShiftPage(shift)} className="tableRow">
                        <td className="tableCell">{shift && shift[378] ? shift[378].value : ''}</td>
                        <td className="tableCell">{shift && shift[321] ? shift[321].value : ''}</td>
                        <td className="tableCell">
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
                            <span style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: statusColor, flexShrink: 0 }}></span>
                            {statusText}
                          </span>
                        </td>
                        <td className="tableCell addressCell">{shift && shift[898] ? shift[898].value : ''}</td>
                        <td className="tableCell">{shift && shift[107] ? shift[107].value : ''}</td>
                        <td className="tableCell">{shift && shift[108] ? shift[108].value : ''}</td>
                        <td className="tableCell">{shift && shift[1295] ? shift[1295].value : ''}</td>
                        <td className="tableCell">{shift && shift[6] ? formatDateTime(shift[6].value) : ''}</td>
                        <td className="tableCell">{shift && shift[7] ? formatDateTime(shift[7].value) : ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination-wrapper">
                <button className={`pagination-btn ${page === 1 ? 'pagination-btn--disabled' : ''}`} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Prev</button>
                {getPageNumbers(page, totalPages).map((p, idx) => (
                  p === '...' ? (
                    <span key={`ellipsis-${idx}`} className="pagination-btn pagination-btn--disabled">...</span>
                  ) : (
                    <button key={p} className={`pagination-btn ${page === p ? 'pagination-btn--active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                  )
                ))}
                <button className={`pagination-btn ${page === totalPages ? 'pagination-btn--disabled' : ''}`} onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
